import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { RbacApi, type AppUser } from "../../admin/rbac/api";
import { useScopeNames } from "../../admin/hierarchy/useScopeNames";
import { useUsersById } from "../../admin/rbac/useUsersById";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { SearchableSelect } from "../../shell/SearchableSelect";
import { ResolutionPanel } from "../../shell/ResolutionPanel";
import { AssignAsTaskButton } from "../../shell/AssignAsTaskButton";
import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import { PerformanceApi, type PerformanceRating, type PerformanceReport, type Classification } from "./api";

export function PerformanceRoutes() {
  return (
    <Routes>
      <Route index element={<List />} />
      <Route path="new" element={<NewReport />} />
      <Route path="mine" element={<MyReports />} />
    </Routes>
  );
}

function List() {
  const [rating, setRating] = useState<PerformanceRating | "">("");
  const [search, setSearch] = useState("");
  const data = useQuery({
    queryKey: ["performance", rating],
    queryFn: () => PerformanceApi.list(rating ? { rating } : undefined),
  });
  useMarkSeen("performance", data.dataUpdatedAt);
  const { name: userName } = useUsersById();
  const { branchName } = useScopeNames();
  const items = (data.data?.items ?? [])
    .filter((r) => !search || (userName(r.subjectUid) + " " + r.reasons).toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Capture Performance</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>Capture how a team member is doing — what's working, what isn't, and what should change.</p>
        </div>
        <span className="row-actions">
          <Link to="mine"><button>My reports</button></Link>
          <Link to="new"><button>New report</button></Link>
        </span>
      </header>

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          Rating:
          <select value={rating} onChange={(e) => setRating(e.target.value as PerformanceRating | "")}>
            <option value="">All</option>
            <option value="failing">Failing</option>
            <option value="poor">Poor</option>
            <option value="satisfactory">Satisfactory</option>
            <option value="good">Good</option>
            <option value="excellent">Excellent</option>
          </select>
        </label>
        <label>
          Search:
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Subject name or reason text…" />
        </label>
        <span />
      </div>

      {data.isLoading && <p>Loading…</p>}
      {data.error && <p className="error">{(data.error as Error).message}</p>}

      <div className="card-grid">
        {items.map((r) => (
          <PerfCard key={r.id} report={r} userName={userName} branchName={branchName} />
        ))}
        {items.length === 0 && !data.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>No matching reports.</div>
        )}
      </div>
    </div>
  );
}

function PerfCard({
  report, userName, branchName,
}: {
  report: PerformanceReport;
  userName: (uid: string | null | undefined) => string;
  branchName: (id: string | null | undefined) => string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="list-card" onClick={() => setOpen(true)} style={{ cursor: "pointer" }}>
        <div className="list-card-header">
          <RatingBadge rating={report.rating} />
        </div>
        <div className="list-card-title">{userName(report.subjectUid)}</div>
        <div className="list-card-snippet">{report.reasons}</div>
        <div className="list-card-foot">
          <span>{branchName(report.branchId)}</span>
          <span>{report.createdAt ? new Date(report.createdAt).toLocaleDateString() : "—"}</span>
        </div>
      </div>
      {open && (
        <div role="dialog" aria-modal="true"
          onClick={(e) => { if (e.currentTarget === e.target) setOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
          <div className="card" style={{ maxWidth: 640, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <RatingBadge rating={report.rating} />
                <h2 style={{ margin: "8px 0 0", fontSize: 18 }}>{userName(report.subjectUid)}</h2>
                <div className="muted" style={{ fontSize: 12 }}>
                  {branchName(report.branchId)} · {report.createdAt ? new Date(report.createdAt).toLocaleString() : "—"}
                </div>
              </div>
              <button className="ghost" onClick={() => setOpen(false)}>Close</button>
            </div>
            <dl className="kv" style={{ marginTop: 16 }}>
              <dt>Reasons</dt><dd style={{ whiteSpace: "pre-wrap" }}>{report.reasons}</dd>
              {report.recommendations && (<><dt>Recommendations</dt><dd style={{ whiteSpace: "pre-wrap" }}>{report.recommendations}</dd></>)}
              <dt>Filed by</dt><dd>{userName(report.reporterUid)}</dd>
              <dt>Classification</dt><dd>{report.classification}</dd>
            </dl>
            <ResolutionPanel
              collection="performanceReports"
              recordId={report.id}
              current={(report as { resolutionStatus?: "pending" | "in_progress" | "resolved" | "false_alarm" }).resolutionStatus}
            />
            <div style={{ marginTop: 12 }}>
              <AssignAsTaskButton
                relatedType="performance"
                relatedId={report.id}
                suggestedTitle={`Action performance report: ${userName(report.subjectUid)}`}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MyReports() {
  const data = useQuery({ queryKey: ["my-performance"], queryFn: PerformanceApi.myReports });
  return (
    <div className="hierarchy">
      <header>
        <Link to=".." className="muted">← All reports</Link>
        <h1 style={{ marginTop: 4 }}>Reports filed against me</h1>
      </header>
      {data.isLoading && <p>Loading…</p>}
      <ul className="tree">
        {data.data?.items.map((r) => (
          <li key={r.id}>
            <div className="row">
              <span>
                <RatingBadge rating={r.rating} />
                <div style={{ marginTop: 4 }}>{r.reasons}</div>
                {r.recommendations && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Recommendations: {r.recommendations}
                  </div>
                )}
              </span>
            </div>
          </li>
        ))}
        {data.data?.items.length === 0 && <li className="muted">Nothing on file. Good.</li>}
      </ul>
    </div>
  );
}

function NewReport() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);

  const [subjectUid, setSubjectUid] = useState("");
  const [rating, setRating] = useState<PerformanceRating>("poor");
  const [reasons, setReasons] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [classification, setClassification] = useState<Classification>("restricted");

  const create = useMutation({
    mutationFn: PerformanceApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["performance"] });
      nav("..", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  function submit() {
    if (!subjectUid || !bid || !reasons) return;
    create.mutate({
      subjectUid,
      branchId: bid,
      rating,
      reasons,
      recommendations: recommendations || undefined,
      classification,
    });
  }

  const userOptions = users.data?.items ?? [];

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!bid,
      blockerHint: "Pick the branch",
      body: (
        <WizardSection title="Where does this performance report apply?" hint="Reports are scoped to a branch.">
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
          />
        </WizardSection>
      ),
    },
    {
      title: "Subject",
      canNext: !!subjectUid,
      blockerHint: "Pick the user this report is about",
      body: (
        <WizardSection title="Who is this report about?" hint="Reports are filed against a specific user account.">
          <WizardField label="Subject" hint="Type to search registered users.">
            <SearchableSelect
              value={subjectUid}
              onChange={setSubjectUid}
              options={userOptions.map((u: AppUser) => ({
                value: u.id,
                label: u.fullName || u.email,
                hint: u.email,
              }))}
              required
            />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Rating",
      canNext: true,
      body: (
        <WizardSection title="Rating + classification">
          <WizardField label="Rating">
            <select value={rating} onChange={(e) => setRating(e.target.value as PerformanceRating)}>
              <option value="failing">Failing</option>
              <option value="poor">Poor</option>
              <option value="satisfactory">Satisfactory</option>
              <option value="good">Good</option>
              <option value="excellent">Excellent</option>
            </select>
          </WizardField>
          <WizardField label="Classification" hint="Restricted by default — performance data is rarely meant for the open record.">
            <select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>
              <option value="unclassified">Unclassified</option>
              <option value="restricted">Restricted</option>
              <option value="secret">Secret</option>
            </select>
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Reasons",
      canNext: !!reasons.trim(),
      blockerHint: "Reasons must explain the rating",
      body: (
        <WizardSection title="Why this rating?" hint="Specific incidents, missed targets, behaviour. Recommendations are optional but help.">
          <WizardField label="Reasons">
            <textarea value={reasons} onChange={(e) => setReasons(e.target.value)} rows={5} placeholder="What you observed and why it warrants this rating." />
          </WizardField>
          <WizardField label="Recommendations (optional)">
            <textarea value={recommendations} onChange={(e) => setRecommendations(e.target.value)} rows={3} placeholder="What should happen next? Pair with mentor, etc." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Review",
      canNext: !!subjectUid && !!bid && !!reasons.trim(),
      body: (
        <WizardSection title="Review and file">
          <dl className="kv">
            <dt>Subject</dt><dd>{userOptions.find((u) => u.id === subjectUid)?.fullName ?? subjectUid}</dd>
            <dt>Rating</dt><dd>{rating}</dd>
            <dt>Classification</dt><dd>{classification}</dd>
            <dt>Branch</dt><dd>{branchesForWid.find((b) => b.id === bid)?.name ?? bid}</dd>
            <dt>Reasons</dt><dd style={{ whiteSpace: "pre-wrap" }}>{reasons}</dd>
            <dt>Recommendations</dt><dd style={{ whiteSpace: "pre-wrap" }}>{recommendations || "—"}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="Capture Performance"
        subtitle="Filed against an existing user. They can see the report."
        steps={steps}
        busy={create.isPending}
        submitLabel="File report"
        onSubmit={submit}
        onCancel={() => nav("..")}
      />
    </div>
  );
}

function RatingBadge({ rating }: { rating: PerformanceRating }) {
  const colors: Record<PerformanceRating, string> = {
    excellent: "#3a8053",
    good: "#3a8053",
    satisfactory: "#a87b1c",
    poor: "#c0531a",
    failing: "#b14444",
  };
  return (
    <span style={{
      marginLeft: 8, fontSize: 11, padding: "2px 6px",
      background: colors[rating], color: "white", borderRadius: 4,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>{rating}</span>
  );
}
